import type { Department, TimelineRow } from './api/types';

// One entry per reason this page can be empty of ClickUp data — a shared
// screen for two of them is what the tester could not tell apart.
export const SETUP_COPY = {
  noSources: {
    title: 'Connect an integration',
    description:
      'ClickUp, Google Calendar or Slack — the day is built from whichever of them is connected.',
  },
} as const;

// The timeline interleaves two integrations, and a row that does not say which
// one it came from reads as a claim Mission Control made on its own. Anything
// not pulled from the calendar arrived over ClickUp — an assigned task, a
// touched task, or time already logged there.
export const TIMELINE_SOURCE_LABELS: Record<TimelineRow['kind'], string> = {
  meeting: 'From Google Calendar',
  task: 'From ClickUp',
  slack: 'From Slack',
  manual: 'Added by you',
};

// A quarter hour is what a person can point at. The day is proposed in half
// hours because a guess should not pretend to be precise, but a correction is
// the CFO saying what happened, and it gets to be finer than the guess.
export const CALENDAR_SNAP_MINUTES = 15;

// One hour of the day, in pixels. Everything else is derived from it, so the
// grid and the blocks cannot drift apart.
export const CALENDAR_HOUR_HEIGHT = 72;

export const CALENDAR_PX_PER_MINUTE = CALENDAR_HOUR_HEIGHT / 60;

// A quarter hour is the shortest block, drawn at its true height so a quarter
// reads as half of a half hour; it holds one tight line. Shorter entries are
// drawn at this height rather than as a sliver.
export const CALENDAR_MIN_BLOCK_HEIGHT = CALENDAR_SNAP_MINUTES * CALENDAR_PX_PER_MINUTE;

// The working day is always drawn, and the day's own content extends it with an
// hour of air either side. Drawing only the hours that already hold something
// costs less ink but leaves nowhere to draw a new entry on a quiet day, and the
// grid is the surface a CFO adds time on.
export const CALENDAR_WINDOW_PAD_MINUTES = 60;
export const CALENDAR_DAY_START_HOUR = 6;
export const CALENDAR_DAY_END_HOUR = 22;

// Content is dropped by what the block can actually show. A half hour is 36px
// and holds one line; three lines need the better part of an hour. Below these
// the block clips mid-sentence, which is what a fixed layout did.
export const CALENDAR_FULL_HEIGHT = 56;
export const CALENDAR_COMPACT_HEIGHT = 28;

// Blocks sharing an hour split its width, and from this many side by side a
// block is too narrow for anything but its title.
export const CALENDAR_TITLE_ONLY_COLUMNS = 3;

// Which integration a block came from, as a glance rather than a line of text.
// The line was the first thing clipped on exactly the short blocks that had no
// room to spare, and colour costs no height at all. It is never the only
// signal: the label stays in the block's title and in the legend above the grid.
//
// `fill` is only reached for a confirmed block. A proposal is drawn dashed and
// unfilled, a logged entry solid and filled, so the two things a CFO needs at a
// glance — where this came from, and whether it is a fact yet — never compete
// for the same ink. A tinted proposal beside a filled entry was too close to
// tell apart on the dark theme.
export const CALENDAR_SOURCE_STYLE: Record<
  TimelineRow['kind'],
  { accent: string; fill: string; edge: string }
> = {
  meeting: {
    accent: 'bg-brand-slate',
    fill: 'bg-brand-slate/25',
    edge: 'border-brand-slate/50',
  },
  task: {
    accent: 'bg-brand-orange',
    fill: 'bg-brand-orange/30',
    edge: 'border-brand-orange/50',
  },
  slack: {
    accent: 'bg-brand-lavender',
    fill: 'bg-brand-lavender/50',
    edge: 'border-brand-lavender/70',
  },
  manual: {
    accent: 'bg-brand-mint',
    fill: 'bg-brand-mint/50',
    edge: 'border-brand-mint/70',
  },
};

export const BLOCK_STATE_LABELS = { logged: 'Logged', proposed: 'To confirm' } as const;

// The legend names the integration, and beside a coloured dot the preposition
// is noise — "From Slack" reads as a sentence where "Slack" reads as a key.
export const CALENDAR_SOURCE_NAMES: Record<TimelineRow['kind'], string> = {
  meeting: 'Google Calendar',
  task: 'ClickUp',
  slack: 'Slack',
  manual: 'Added by you',
};

// The address names what is switched off rather than on, so a bare link shows
// the whole day and a source added later is on without anyone opting in.
export const HIDDEN_SOURCES_PARAM = 'hide';

// Analysing a day reads ClickUp, the calendar and Slack, then asks a model to
// group what it found — dozens of upstream calls and two generations. The
// shared client gives up at 30s, well before the server does, so the CFO was
// shown a failure for work that had actually succeeded and been stored. Four
// minutes sits inside Cloud Run's own 300s request ceiling.
export const ANALYSE_TIMEOUT_MS = 240_000;

// Below this a press is a click, above it a drag. A block is both a button and
// something to move, and without a threshold a slightly shaky click would slide
// the block instead of opening it.
export const MOVE_THRESHOLD_PX = 4;

// Messages closer together than this from one person are one turn. Five
// minutes is roughly where a follow-up stops reading as the same thought.
export const TRANSCRIPT_RUN_GAP_MS = 5 * 60_000;

// How often the queued analysis is asked how it is getting on. Short enough
// that a fast day still feels immediate, long enough that a slow one is not
// answered a hundred times.
export const ANALYSE_POLL_MS = 2_000;

// Temporary, until Mission Control is stable: an admin opens a member's day as
// that member sees it, to reproduce what they report without their login. It
// only reads. Removing it means deleting this param and every `viewAs` it feeds.
export const VIEW_AS_PARAM = 'as';

// Client work first: it is the default, and most of a CFO's day.
export const DEPARTMENT_OPTIONS: ReadonlyArray<{ value: Department; label: string }> = [
  { value: 'csd', label: 'Client Service Delivery' },
  { value: 'rnd', label: 'R&D' },
  { value: 'gtm', label: 'GTM' },
  { value: 'ops', label: 'Ops' },
];
